import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
    ChildrenService,
    UpdateChildPayload
} from '@/shared/api/rest/services/children.service'
import { useAlertService } from '@/shared/lib/useAlertService'

/**
 * Correct a child's details from the teacher registry.
 *
 * The card is rendered from the children embedded in the `GET /parents` page,
 * so that list — not the single `GET /children/:id` record — is what has to be
 * refetched for a correction to show. `child` is invalidated too, because the
 * parent-facing edit page reads the record on its own.
 */
export const useUpdateChild = () => {
    const queryClient = useQueryClient()
    const alertService = useAlertService()

    const mutation = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: UpdateChildPayload }) =>
            ChildrenService.update(id, payload),
        onSuccess: (_result, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['registry-parents'] })
            queryClient.invalidateQueries({ queryKey: ['child', id] })
            alertService.success('Child details updated.')
        },
        onError: (error: Error) => {
            // The backend answers a name/DOB clash with 409 and a readable
            // message; surface it as-is rather than a generic failure.
            alertService.error(error.message, 'Could not save changes')
        }
    })

    return { updateChild: mutation.mutateAsync, saving: mutation.isPending }
}
